/**
 * IntersectionObserver-driven scroll reveals (Prestige / David Webb–style product entrances).
 * Exposes window.themeScrollRevealInit(root?) for AJAX sections (facets, recommendations).
 * Elements in the viewport on first paint reveal immediately; below-the-fold items reveal on scroll.
 */
(function () {
  const IO_OPTS = { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.12 };
  const IO_OPTS_PRODUCT_GRID = { root: null, rootMargin: '0px 0px 15% 0px', threshold: 0.04 };
  const HERO_SYNC_SELECTOR = '.js-hero-sync-reveal';
  const HERO_SYNC_TIMEOUT_MS = 5000;
  const syncPending = new WeakSet();
  let userHasScrolled = false;
  let scrollFlushRaf = 0;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function animationsDisabled() {
    if (prefersReducedMotion()) return true;
    if (document.body.classList.contains('theme-animations-off')) return true;
    if (window.theme && window.theme.animationsEnable === false) return true;
    return false;
  }

  function isDesignMode() {
    return document.documentElement.classList.contains('shopify-design-mode');
  }

  function isProductCardReveal(el) {
    return el.classList.contains('card-product');
  }

  function isInSliderGrid(el) {
    return Boolean(el.closest('.slider-element, .slider-grid'));
  }

  function isInRevealViewport(el) {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    let verticallyVisible;

    if (isProductCardReveal(el)) {
      verticallyVisible = rect.top < vh && rect.bottom > 0;
    } else {
      verticallyVisible = rect.top < vh * 0.92 && rect.bottom > vh * 0.08;
    }

    if (!verticallyVisible) return false;
    if (isInSliderGrid(el)) {
      return rect.left < vw && rect.right > 0;
    }
    return true;
  }

  function shouldRevealOnIntersect(el) {
    if (isDesignMode()) return true;
    if (isProductCardReveal(el)) return isInRevealViewport(el);
    if (userHasScrolled) return true;
    return isInRevealViewport(el);
  }

  function bindScrollFlush() {
    window.addEventListener('scroll', scheduleScrollFlush, { passive: true });
  }

  function scheduleScrollFlush() {
    if (!userHasScrolled || scrollFlushRaf) return;
    scrollFlushRaf = requestAnimationFrame(() => {
      scrollFlushRaf = 0;
      flushVisibleReveals(document);
      flushVisibleBannerTextFade(document);
    });
  }

  function unlockScrollReveal() {
    if (userHasScrolled) return;
    userHasScrolled = true;
    flushVisibleReveals(document);
    flushVisibleBannerTextFade(document);
    bindScrollFlush();
  }

  function bindScrollUnlock() {
    if (window.scrollY > 0) {
      userHasScrolled = true;
      flushVisibleReveals(document);
      flushVisibleBannerTextFade(document);
      bindScrollFlush();
      return;
    }

    window.addEventListener('scroll', unlockScrollReveal, { passive: true, once: true });
    window.addEventListener('wheel', unlockScrollReveal, { passive: true, once: true });
    window.addEventListener('touchmove', unlockScrollReveal, { passive: true, once: true });
    window.addEventListener(
      'keydown',
      (event) => {
        const keys = ['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', ' ', 'Home', 'End'];
        if (keys.includes(event.key)) unlockScrollReveal();
      },
      { once: true }
    );
  }

  function markAllVisible(scope) {
    const root = scope && scope.nodeType === 1 ? scope : document;
    root.querySelectorAll('.js-scroll-reveal').forEach((el) => {
      el.classList.add('js-scroll-reveal--visible');
      el.dataset.srObserved = 'true';
    });
  }

  function flushVisibleReveals(scope) {
    const root = scope && scope.nodeType === 1 ? scope : document;
    root.querySelectorAll('.js-scroll-reveal:not(.js-scroll-reveal--visible)').forEach((el) => {
      if (el.closest(`${HERO_SYNC_SELECTOR}:not(.is-hero-ready)`)) return;
      if (isInRevealViewport(el)) {
        el.classList.add('js-scroll-reveal--visible');
        el.dataset.srObserved = 'true';
      }
    });
  }

  function markBlurLoaded(img) {
    img.classList.add('is-loaded');
    const wrap = img.closest('.theme-blur-up');
    if (wrap) wrap.classList.add('is-loaded');
  }

  function revealHeroSection(section) {
    section.classList.add('is-hero-ready');
    section.querySelectorAll('.theme-blur-up__img').forEach(markBlurLoaded);
    section.querySelectorAll('.js-scroll-reveal').forEach((el) => {
      el.classList.add('js-scroll-reveal--visible');
      el.dataset.srObserved = 'true';
    });
  }

  function syncHeroReveal(scope) {
    const root = scope && scope.nodeType === 1 ? scope : document;
    root.querySelectorAll(`${HERO_SYNC_SELECTOR}:not(.is-hero-ready)`).forEach((section) => {
      if (syncPending.has(section)) return;
      syncPending.add(section);

      if (animationsDisabled() || isDesignMode()) {
        revealHeroSection(section);
        return;
      }

      const imgs = section.querySelectorAll('img');
      if (!imgs.length) {
        revealHeroSection(section);
        return;
      }

      let loaded = 0;
      let revealed = false;
      const total = imgs.length;

      function finish() {
        if (revealed) return;
        revealed = true;
        revealHeroSection(section);
      }

      const timeout = window.setTimeout(finish, HERO_SYNC_TIMEOUT_MS);

      function onImgDone() {
        loaded += 1;
        if (loaded >= total) {
          window.clearTimeout(timeout);
          finish();
        }
      }

      imgs.forEach((img) => {
        if (img.complete && img.naturalWidth > 0) onImgDone();
        else {
          img.addEventListener('load', onImgDone, { once: true });
          img.addEventListener('error', onImgDone, { once: true });
        }
      });
    });
  }

  function createRevealObserver(opts) {
    return new IntersectionObserver((entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (!shouldRevealOnIntersect(entry.target)) continue;
        entry.target.classList.add('js-scroll-reveal--visible');
        obs.unobserve(entry.target);
      }
    }, opts);
  }

  function observeIn(scope) {
    const root = scope && scope.nodeType === 1 ? scope : document;
    const targets = Array.from(
      root.querySelectorAll('.js-scroll-reveal:not(.js-scroll-reveal--visible):not([data-sr-observed])')
    ).filter((el) => !el.closest(`${HERO_SYNC_SELECTOR}:not(.is-hero-ready)`));
    if (!targets.length) return;

    const gridTargets = targets.filter((el) => isProductCardReveal(el));
    const otherTargets = targets.filter((el) => !isProductCardReveal(el));

    if (gridTargets.length) {
      const gridIo = createRevealObserver(IO_OPTS_PRODUCT_GRID);
      gridTargets.forEach((el) => {
        el.dataset.srObserved = 'true';
        gridIo.observe(el);
      });
    }

    if (otherTargets.length) {
      const io = createRevealObserver(IO_OPTS);
      otherTargets.forEach((el) => {
        el.dataset.srObserved = 'true';
        io.observe(el);
      });
    }
  }

  function themeScrollRevealInit(scope) {
    if (animationsDisabled() || isDesignMode()) {
      markAllVisible(scope);
      return;
    }
    flushVisibleReveals(scope);
    observeIn(scope);
  }

  window.themeScrollRevealInit = themeScrollRevealInit;

  function markAllBannerTextFade(scope) {
    const root = scope && scope.nodeType === 1 ? scope : document;
    root.querySelectorAll('.banner-text-fade-in').forEach((el) => {
      el.classList.add('banner-text-fade-in--active');
      el.dataset.bannerFadeObserved = 'true';
    });
  }

  function flushVisibleBannerTextFade(scope) {
    const root = scope && scope.nodeType === 1 ? scope : document;
    root.querySelectorAll('.banner-text-fade-in:not(.banner-text-fade-in--active)').forEach((el) => {
      if (isInRevealViewport(el)) {
        el.classList.add('banner-text-fade-in--active');
        el.dataset.bannerFadeObserved = 'true';
      }
    });
  }

  function observeBannerTextFade(scope) {
    const root = scope && scope.nodeType === 1 ? scope : document;
    const targets = Array.from(
      root.querySelectorAll('.banner-text-fade-in:not(.banner-text-fade-in--active):not([data-banner-fade-observed])')
    );
    if (!targets.length) return;

    const io = new IntersectionObserver((entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (!shouldRevealOnIntersect(entry.target)) continue;
        entry.target.classList.add('banner-text-fade-in--active');
        obs.unobserve(entry.target);
      }
    }, IO_OPTS);

    targets.forEach((el) => {
      el.dataset.bannerFadeObserved = 'true';
      io.observe(el);
    });
  }

  function themeBannerTextFadeInit(scope) {
    if (animationsDisabled() || isDesignMode()) {
      markAllBannerTextFade(scope);
      return;
    }
    flushVisibleBannerTextFade(scope);
    observeBannerTextFade(scope);
  }

  window.themeBannerTextFadeInit = themeBannerTextFadeInit;

  function boot(scope) {
    syncHeroReveal(scope);
    themeScrollRevealInit(scope);
    themeBannerTextFadeInit(scope);
    if (!animationsDisabled() && !isDesignMode()) {
      requestAnimationFrame(() => {
        flushVisibleReveals(scope);
        flushVisibleBannerTextFade(scope);
      });
    }
  }

  bindScrollUnlock();

  document.addEventListener('DOMContentLoaded', () => {
    boot(document);
  });

  window.addEventListener('load', () => {
    boot(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && event.target.nodeType === 1) {
      boot(event.target);
    }
  });
})();
